import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantsService } from '../api/services/tenants.service';
import {
  FloatingMenuItem,
  FloatingMenuPatchRequest,
  DEFAULT_FLOATING_MENU,
} from '../api/types/tenants.types';
import { useCallback } from 'react';

/**
 * React Query key for floating menu status
 */
export const FLOATING_MENU_QUERY_KEY = ['floating-menu-status'];

/**
 * Hook for fetching floating menu items
 * Returns menu array with code, icon, and status for each item
 */
export const useFloatingMenuStatus = () => {
  return useQuery({
    queryKey: FLOATING_MENU_QUERY_KEY,
    queryFn: async (): Promise<FloatingMenuItem[]> => {
      try {
        const response = await tenantsService.getFloatingMenuStatus();
        // Use new menu array from API
        return response.menu || DEFAULT_FLOATING_MENU;
      } catch (error) {
        // Return default if API fails (404, 500, etc.)
        return DEFAULT_FLOATING_MENU;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (previously cacheTime)
    retry: false, // Don't retry failed requests
    // Initial data while loading
    placeholderData: DEFAULT_FLOATING_MENU,
  });
};

/**
 * Hook for updating floating menu status
 * Includes optimistic updates for instant UI feedback
 */
export const useUpdateFloatingMenu = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: { code: string; status: boolean }) => {
      const response = await tenantsService.updateFloatingMenuStatus({
        status: { [updates.code]: updates.status },
      } as FloatingMenuPatchRequest);
      return response.menu || [];
    },
    // Optimistic update
    onMutate: async (updates) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: FLOATING_MENU_QUERY_KEY });

      // Snapshot previous value
      const previousMenu = queryClient.getQueryData<FloatingMenuItem[]>(
        FLOATING_MENU_QUERY_KEY
      );

      // Optimistically update cache
      if (previousMenu) {
        const newMenu = previousMenu.map((item) =>
          item.code === updates.code
            ? { ...item, status: updates.status }
            : item
        );
        queryClient.setQueryData<FloatingMenuItem[]>(
          FLOATING_MENU_QUERY_KEY,
          newMenu
        );
      }

      return { previousMenu };
    },
    // Rollback on error
    onError: (_err, _updates, context) => {
      if (context?.previousMenu) {
        queryClient.setQueryData(FLOATING_MENU_QUERY_KEY, context.previousMenu);
      }
    },
    // Refetch on success to get fresh data
    onSuccess: (data) => {
      if (data.length > 0) {
        queryClient.setQueryData(FLOATING_MENU_QUERY_KEY, data);
      }
    },
  });
};

/**
 * Hook for resetting floating menu status to default
 */
export const useResetFloatingMenu = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await tenantsService.resetFloatingMenuStatus();
      return response.menu || DEFAULT_FLOATING_MENU;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(FLOATING_MENU_QUERY_KEY, data);
    },
  });
};

/**
 * Convenience hook that combines all floating menu operations
 * Returns menu items array with code, icon, and status
 */
export const useFloatingMenu = () => {
  const { data: menuItems, isLoading, error } = useFloatingMenuStatus();
  const updateMutation = useUpdateFloatingMenu();
  const resetMutation = useResetFloatingMenu();

  /**
   * Toggle a single menu item by code
   * Sends immediate API call
   */
  const toggle = useCallback(
    (code: string) => {
      if (!menuItems) return;

      const item = menuItems.find((m) => m.code === code);
      if (item) {
        updateMutation.mutate({ code, status: !item.status });
      }
    },
    [menuItems, updateMutation]
  );

  /**
   * Get status of a specific menu item
   */
  const getStatus = useCallback(
    (code: string): boolean => {
      const item = menuItems?.find((m) => m.code === code);
      return item?.status ?? false;
    },
    [menuItems]
  );

  /**
   * Check if a menu item exists
   */
  const hasItem = useCallback(
    (code: string): boolean => {
      return menuItems?.some((m) => m.code === code) ?? false;
    },
    [menuItems]
  );

  /**
   * Reset to default values
   */
  const reset = useCallback(() => {
    resetMutation.mutate();
  }, [resetMutation]);

  return {
    /** Menu items array - use this for rendering */
    menuItems: menuItems || DEFAULT_FLOATING_MENU,
    isLoading,
    error,
    /** Toggle item status by code */
    toggle,
    /** Get status of specific item */
    getStatus,
    /** Check if item exists in menu */
    hasItem,
    reset,
    isUpdating: updateMutation.isPending,
    isResetting: resetMutation.isPending,
  };
};
